import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

/**
 * The field home's tile grid: two columns of tiles, each an icon, a title and
 * a one-line subtitle, opening a screen.
 *
 * Lifted out of FieldHomeScreen (fix/field-home-trucking-rentals) so the
 * casing home and the trucking / rentals home (pages/my-work/FieldHomePage)
 * draw it from one component. The markup is casing's, byte for byte:
 * FieldHomeScreen.unchanged.test.tsx compares that screen against snapshots
 * written from origin/main before the lift.
 *
 * Subtitles stay short: a half-width tile at 390px fits roughly 27 characters,
 * and longer ones wrap to three lines and stop the rows lining up.
 */
export interface FieldTile {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  path: string;
  /** A text colour for the icon only; the labels carry no colour of their own. */
  color: string;
}

export default function FieldTileGrid({ tiles }: { tiles: FieldTile[] }) {
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-2 gap-3 items-stretch">
      {tiles.map((tile) => {
        const Icon = tile.icon;
        return (
          <button
            key={tile.path}
            onClick={() => navigate(tile.path)}
            className="h-full bg-fieldiq-card border-2 border-fieldiq-border rounded-xl p-4 text-center card-hover active:scale-[0.97] transition-transform flex flex-col items-center justify-start gap-1"
          >
            <Icon size={28} className={tile.color} />
            <span className="text-sm font-semibold leading-tight">{tile.title}</span>
            <span className="text-xs text-fieldiq-text leading-snug">{tile.subtitle}</span>
          </button>
        );
      })}
    </div>
  );
}
